import React from 'react';
import type { SchemaScore } from '../types';

interface ScoreCardProps {
  score: SchemaScore;
}

/**
 * Score ring + label + breakdown for the page's existing schema implementation.
 * Matches the editorial paper/ink aesthetic and reuses the same ring styling
 * as the semantic-html-tool.
 */
export const ScoreCard: React.FC<ScoreCardProps> = ({ score }) => {
  const ringColor = ringStrokeForLabel(score.label);
  const labelColor = labelColorForLabel(score.label);

  return (
    <div className="card" style={{ marginBottom: 24 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 24, flexWrap: 'wrap' }}>
        <ScoreRing score={score.overall} color={ringColor} />
        <div style={{ flex: 1, minWidth: 220 }}>
          <span className="eyebrow" style={{ display: 'block', marginBottom: 6 }}>
            Schema implementation score
          </span>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 12, flexWrap: 'wrap' }}>
            <strong
              style={{
                fontFamily: 'var(--font-display)',
                fontSize: 32,
                fontWeight: 500,
                color: 'var(--ink)',
                lineHeight: 1,
              }}
            >
              {score.overall}
              <span style={{ fontSize: 18, color: 'var(--ink-3)', fontWeight: 400 }}> / 100</span>
            </strong>
            <span
              style={{
                fontSize: 14,
                fontWeight: 600,
                color: labelColor,
                textTransform: 'uppercase',
                letterSpacing: '0.06em',
              }}
            >
              {score.label}
            </span>
          </div>
          <BreakdownBars breakdown={score.breakdown} />
        </div>
      </div>

      {score.notes.length > 0 && (
        <ul style={{ margin: '20px 0 0', paddingLeft: 20, fontSize: 13.5, color: 'var(--ink-2)', lineHeight: 1.6 }}>
          {score.notes.map((n, i) => (
            <li key={i} style={{ marginBottom: 4 }}>
              <span
                aria-hidden
                style={{
                  display: 'inline-block',
                  marginRight: 6,
                  color: n.kind === 'win' ? 'var(--score-high)' : 'var(--score-mid)',
                  fontWeight: 700,
                }}
              >
                {n.kind === 'win' ? '✓' : '!'}
              </span>
              {n.message}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

const ScoreRing: React.FC<{ score: number; color: string }> = ({ score, color }) => {
  const size = 96;
  const stroke = 8;
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const dash = (score / 100) * c;
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} aria-hidden>
      <circle cx={size / 2} cy={size / 2} r={r} fill="none" className="score-ring-track" strokeWidth={stroke} />
      <circle
        cx={size / 2}
        cy={size / 2}
        r={r}
        fill="none"
        stroke={color}
        strokeWidth={stroke}
        strokeDasharray={`${dash} ${c}`}
        strokeLinecap="round"
        transform={`rotate(-90 ${size / 2} ${size / 2})`}
        className="score-ring-fill"
      />
      <text
        x={size / 2}
        y={size / 2 + 6}
        textAnchor="middle"
        fontFamily="var(--font-display)"
        fontSize={26}
        fontWeight={500}
        fill="var(--ink)"
      >
        {score}
      </text>
    </svg>
  );
};

const BreakdownBars: React.FC<{ breakdown: SchemaScore['breakdown'] }> = ({ breakdown }) => (
  <div style={{ marginTop: 14, display: 'grid', gap: 8, maxWidth: 420 }}>
    <Bar label="Coverage" value={breakdown.coverage} hint="High-confidence types implemented" />
    <Bar label="Completeness" value={breakdown.completeness} hint="Required + recommended fields filled" />
    <Bar label="Google rich results" value={breakdown.googleEligibility} hint="Eligible types implemented" />
  </div>
);

const Bar: React.FC<{ label: string; value: number; hint: string }> = ({ label, value, hint }) => {
  const fill = value >= 80 ? 'is-high' : value >= 60 ? 'is-mid' : 'is-low';
  return (
    <div title={hint}>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
        <span style={{ fontSize: 12, color: 'var(--ink-3)', fontFamily: 'var(--font-body)' }}>{label}</span>
        <span style={{ fontSize: 12, color: 'var(--ink-2)', fontFamily: 'var(--font-mono)' }}>{value}%</span>
      </div>
      <span className="confidence-track">
        <span className={`confidence-fill ${fill}`} style={{ width: `${value}%` }} />
      </span>
    </div>
  );
};

function ringStrokeForLabel(label: SchemaScore['label']): string {
  switch (label) {
    case 'Excellent': return 'var(--score-high)';
    case 'Good': return 'var(--signal)';
    case 'Needs work': return 'var(--score-mid)';
    case 'Poor': return 'var(--score-low)';
  }
}

function labelColorForLabel(label: SchemaScore['label']): string {
  switch (label) {
    case 'Excellent': return 'var(--score-high)';
    case 'Good': return 'var(--signal-ink)';
    case 'Needs work': return 'var(--score-mid)';
    case 'Poor': return 'var(--score-low)';
  }
}
